import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import citiesService from '../../services/citiesService';

const initialState = {
  cities: [],
  totalCities: 0,
  totalPages: 1,
  isLoading: false,
  error: null,
  success: false,
};

export const fetchCities = createAsyncThunk(
  'cities/fetchAll',
  async (params, thunkAPI) => {
    try {
      return await citiesService.getAll(params);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch cities';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const fetchCitiesByState = createAsyncThunk(
  'cities/fetchByState',
  async (stateId, thunkAPI) => {
    try {
      return await citiesService.getByState(stateId);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch cities';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const createCity = createAsyncThunk(
  'cities/create',
  async (data, thunkAPI) => {
    try {
      return await citiesService.create(data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create city';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateCity = createAsyncThunk(
  'cities/update',
  async ({ id, data }, thunkAPI) => {
    try {
      return await citiesService.update(id, data);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update city';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteCity = createAsyncThunk(
  'cities/delete',
  async (id, thunkAPI) => {
    try {
      await citiesService.delete(id);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete city';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const citiesSlice = createSlice({
  name: 'cities',
  initialState,
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cities = action.payload.cities;
        state.totalCities = action.payload.pagination.total;
        state.totalPages = action.payload.pagination.pages;
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCitiesByState.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCitiesByState.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cities = action.payload;
      })
      .addCase(fetchCitiesByState.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createCity.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createCity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cities.push(action.payload);
        state.success = true;
      })
      .addCase(createCity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateCity.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateCity.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.cities.findIndex((c) => c._id === action.payload._id);
        if (index !== -1) {
          state.cities[index] = action.payload;
        }
        state.success = true;
      })
      .addCase(updateCity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteCity.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteCity.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cities = state.cities.filter((c) => c._id !== action.payload);
        state.success = true;
      })
      .addCase(deleteCity.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetSuccess, clearError } = citiesSlice.actions;
export default citiesSlice.reducer;
